import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  DatePicker,
  Select,
  InputNumber,
  Input,
  message,
} from "antd";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { BASE_API_URL } from "../../../utils/consts";
import { isLocationActive } from "../../../utils/helpers/locationVisibility";

interface AddExtraIncomeModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedDate?: Dayjs;
  locationId?: string;
  editingEntry?: {
    _id: string;
    date: string;
    amount: number;
    description: string;
    typeId: { _id: string; name: string; color: string };
    locationId: { _id: string; name: string };
  } | null;
}

interface ExtraIncomeType {
  _id: string;
  name: string;
  color: string;
}

const { TextArea } = Input;

const AddExtraIncomeModal: React.FC<AddExtraIncomeModalProps> = ({
  visible,
  onClose,
  onSuccess,
  selectedDate,
  locationId,
  editingEntry,
}) => {
  const [form] = Form.useForm();
  const userData = useSelector((state: any) => state.userData.user);
  const [extraIncomeTypes, setExtraIncomeTypes] = useState<ExtraIncomeType[]>(
    []
  );
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      if (!userData?.clientId) return;

      try {
        const response = await axios.get(
          `${BASE_API_URL}/${userData.clientId}/locations`
        );
        setLocations((response.data || []).filter(isLocationActive));
      } catch (error) {
        console.error("Error fetching locations:", error);
        setLocations([]);
      }
    };

    if (visible) {
      fetchLocations();
    }
  }, [visible, userData?.clientId]);

  // Fetch extra income types on mount
  useEffect(() => {
    const fetchTypes = async () => {
      if (!userData?.clientId) return;

      try {
        const response = await axios.get(
          `${BASE_API_URL}/extra-income-types/${userData.clientId}`
        );

        // Backend returns { success: true, types: [...] }
        const types = response.data.types || response.data || [];
        const typesArray = Array.isArray(types) ? types : [];

        setExtraIncomeTypes(typesArray);
      } catch (error) {
        console.error("Error fetching extra income types:", error);
        message.error("Greška pri učitavanju tipova prihoda");
        setExtraIncomeTypes([]);
      }
    };

    if (visible) {
      fetchTypes();
    }
  }, [visible, userData?.clientId]);

  // Set initial form values when modal opens
  useEffect(() => {
    if (visible) {
      if (editingEntry) {
        // Edit mode - populate with existing data
        form.setFieldsValue({
          date: dayjs(editingEntry.date),
          locationId: editingEntry.locationId._id,
          typeId: editingEntry.typeId._id,
          amount: editingEntry.amount,
          description: editingEntry.description || "",
        });
      } else {
        // Add mode - set defaults
        form.setFieldsValue({
          date: selectedDate || dayjs(),
          locationId: locationId || undefined,
          typeId: undefined,
          amount: undefined,
          description: "",
        });
      }
    }
  }, [visible, selectedDate, locationId, editingEntry, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      if (editingEntry) {
        // Update existing entry
        await axios.put(`${BASE_API_URL}/extra-income/${editingEntry._id}`, {
          date: values.date.format("YYYY-MM-DD"),
          locationId: values.locationId,
          typeId: values.typeId,
          amount: values.amount,
          description: values.description || "",
        });
        message.success("Dodatni prihod je uspešno ažuriran");
      } else {
        // Create new entry
        await axios.post(`${BASE_API_URL}/extra-income`, {
          clientId: userData.clientId,
          locationId: values.locationId,
          date: values.date.format("YYYY-MM-DD"),
          typeId: values.typeId,
          amount: values.amount,
          description: values.description || "",
        });
        message.success("Dodatni prihod je uspešno dodat");
      }
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        // Validation error
        return;
      }
      console.error("Error saving extra income:", error);
      message.error(
        editingEntry
          ? "Greška pri ažuriranju dodatnog prihoda"
          : "Greška pri kreiranju dodatnog prihoda"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={editingEntry ? "Izmeni dodatni prihod" : "Dodaj dodatni prihod"}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={editingEntry ? "Sačuvaj" : "Dodaj"}
      cancelText="Otkaži"
      confirmLoading={loading}
      width={600}
      className="standardHeader standardFooter"
    >
      <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
        <Form.Item
          label="Datum"
          name="date"
          rules={[{ required: true, message: "Izaberite datum" }]}
        >
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
        </Form.Item>

        <Form.Item
          label="Lokacija"
          name="locationId"
          rules={[{ required: true, message: "Izaberite lokaciju" }]}
        >
          <Select placeholder="Izaberite lokaciju">
            {locations.map((location: any) => (
              <Select.Option key={location._id} value={location._id}>
                {location.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Tip prihoda"
          name="typeId"
          rules={[{ required: true, message: "Izaberite tip prihoda" }]}
        >
          <Select placeholder="Izaberite tip dodatnog prihoda">
            {extraIncomeTypes.map((type) => (
              <Select.Option key={type._id} value={type._id}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: type.color,
                      marginRight: 8,
                    }}
                  />
                  {type.name}
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Iznos"
          name="amount"
          rules={[
            { required: true, message: "Unesite iznos" },
            {
              type: "number",
              min: 0,
              message: "Iznos mora biti pozitivan broj",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Unesite iznos"
            precision={2}
            min={0}
          />
        </Form.Item>

        <Form.Item label="Opis" name="description">
          <TextArea
            rows={3}
            placeholder="Opis dodatnog prihoda (opcionalno)"
            maxLength={500}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddExtraIncomeModal;
